export type AgentModelOption = {
  id: string;
  label: string;
};

export type AgentModelSurface = {
  key: string;
  label: string;
  routeHint: 'chat' | 'tickets' | 'auto-merge';
};

export const DEFAULT_AGENT_MODEL_ID = 'google-genai:gemini-2.5-flash-lite';

export const AGENT_MODEL_OPTIONS: AgentModelOption[] = [
  { id: 'anthropic:claude-opus-4-6', label: 'Claude Opus 4.6' },
  { id: 'anthropic:claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'anthropic:claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { id: 'anthropic:claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
  { id: 'anthropic:claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
  { id: 'anthropic:claude-opus-4-1-20250805', label: 'Claude Opus 4.1' },
  { id: 'anthropic:claude-opus-4-20250514', label: 'Claude Opus 4' },
  { id: 'anthropic:claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
  { id: 'anthropic:claude-3-haiku-20240307', label: 'Claude 3 Haiku (legacy)' },
  { id: 'google-genai:gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { id: 'google-genai:gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { id: 'google-genai:gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'google-genai:gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (preview)' },
  { id: 'google-genai:gemini-3-flash-preview', label: 'Gemini 3 Flash (preview)' },
  { id: 'google-genai:gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash Lite (preview)' },
  { id: 'google-genai:gemini-3-flash', label: 'Gemini 3 Flash (legacy alias)' },
  { id: 'openai:gpt-5.4', label: 'OpenAI GPT-5.4' },
  { id: 'openai:gpt-5.4-mini', label: 'OpenAI GPT-5.4 mini' },
  { id: 'openai:gpt-5.4-nano', label: 'OpenAI GPT-5.4 nano' },
  { id: 'openai:gpt-4o-mini', label: 'OpenAI GPT-4o mini' },
  { id: 'openai:gpt-4o', label: 'OpenAI GPT-4o' },
  { id: 'xai:grok-4.20-0309-reasoning', label: 'Grok 4.20 (reasoning)' },
  { id: 'xai:grok-4.20-0309-non-reasoning', label: 'Grok 4.20 (non-reasoning)' },
  { id: 'xai:grok-4-1-fast-reasoning', label: 'Grok 4.1 Fast (reasoning)' },
  { id: 'xai:grok-4-1-fast-non-reasoning', label: 'Grok 4.1 Fast (non-reasoning)' },
  { id: 'xai:grok-4-fast-non-reasoning', label: 'Grok 4 Fast (non-reasoning)' },
  { id: 'xai:grok-4-fast-reasoning', label: 'Grok 4 Fast (reasoning)' },
  { id: 'xai:grok-3', label: 'Grok 3' },
  { id: 'xai:grok-2-vision-1212', label: 'Grok 2 Vision (1212)' },
];

export const AGENT_MODEL_SURFACES: AgentModelSurface[] = [
  { key: 'chat.global', label: 'Global assistant', routeHint: 'chat' },
  { key: 'chat.architecture', label: 'Architecture chat', routeHint: 'chat' },
  { key: 'chat.audit', label: 'Audit chat', routeHint: 'chat' },
  { key: 'chat.code', label: 'Code chat', routeHint: 'chat' },
  { key: 'chat.compliance', label: 'Compliance chat', routeHint: 'chat' },
  { key: 'chat.db-design', label: 'DB Design chat', routeHint: 'chat' },
  { key: 'chat.define', label: 'Define chat', routeHint: 'chat' },
  { key: 'chat.design', label: 'Design chat', routeHint: 'chat' },
  { key: 'chat.devops', label: 'DevOps chat', routeHint: 'chat' },
  { key: 'chat.docs', label: 'Docs chat', routeHint: 'chat' },
  { key: 'chat.guardrails', label: 'Guardrails chat', routeHint: 'chat' },
  { key: 'chat.kpis', label: 'KPIs chat', routeHint: 'chat' },
  { key: 'chat.optimization', label: 'Optimization chat', routeHint: 'chat' },
  { key: 'chat.project', label: 'Project chat', routeHint: 'chat' },
  { key: 'chat.quality', label: 'Quality chat', routeHint: 'chat' },
  { key: 'chat.quotations', label: 'Quotations chat', routeHint: 'chat' },
  { key: 'chat.repos', label: 'Repos chat', routeHint: 'chat' },
  { key: 'chat.stats', label: 'Stats chat', routeHint: 'chat' },
  { key: 'chat.testing', label: 'Testing chat', routeHint: 'chat' },
  { key: 'chat.tickets', label: 'Tickets chat', routeHint: 'tickets' },
  { key: 'chat.whatsapp', label: 'WhatsApp chat', routeHint: 'chat' },
  { key: 'tickets.execute', label: 'Ticket execution agent', routeHint: 'tickets' },
  { key: 'tickets.auto-merge', label: 'Ticket auto-merge review', routeHint: 'auto-merge' },
];

export function normalizeAgentModelId(input: string | null | undefined): string {
  const raw = String(input ?? '').trim();
  if (!raw) return '';
  if (!raw.includes(':') && raw.startsWith('gemini-')) return `google-genai:${raw}`;
  return raw;
}

export function isKnownAgentModelId(modelId: string | null | undefined): boolean {
  const normalized = normalizeAgentModelId(modelId);
  return AGENT_MODEL_OPTIONS.some((option) => normalizeAgentModelId(option.id) === normalized);
}

function surfaceModelStorageKey(surfaceKey: string): string {
  const suffix = String(surfaceKey ?? '').trim() || 'chat.default';
  return `agent.surfaceModelOverride.${suffix}`;
}

export function chatSurfaceKeyFromPage(page: string | null | undefined): string {
  const normalizedPage = String(page ?? '').trim() || 'default';
  return `chat.${normalizedPage}`;
}
